import * as React from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Order } from "@/types/AppTypes";
import { parseJSONField } from "@/utils/helper/parseJSON";

type Sale = {
  order_id: string;
  product: string;
  date: string;
  quantity: number;
  amount: string;
};

const columns: ColumnDef<Order>[] = [
  {
    accessorKey: "order_id",
    header: "Order ID",
    cell: ({ row }) => row.original?.id?.slice(0, 6),
  },
  {
    accessorKey: "product",
    header: "Product",
    cell: ({ row }) => {
      const variant = parseJSONField(row?.original?.variant);

      return <span>{variant?.name},</span>;
    },
  },
  {
    accessorKey: "date",
    header: "Date",
    cell: ({ row }) => {
      const isoDate = row.original?.created_at;
      if (!isoDate) return "-";
      const date = new Date(isoDate);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
  },
  {
    accessorKey: "quantity",
    header: "Quantity",
    cell: ({ row }) => row.original?.quantity,
  },
  {
    accessorKey: "amount",
    header: "Amount",
    cell: ({ row }) => {
      const variant = parseJSONField(row?.original?.variant);
      const amount = (
        (variant?.price ?? 0) * (row.original?.quantity ?? 0)
      ).toFixed(2);
      return <span>{amount}</span>;
    },
  },
];
type AnalyticsDisplayTableProps = {
  data: Order[];
  children: React.ReactNode;
};
const AnalyticsDataTable = ({ data, children }: AnalyticsDisplayTableProps) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      pagination,
    },
    filterFns: {
      categoryFilter: (row, columnId, filterValue) => {
        const value = row.getValue(columnId);
        return Boolean(
          value &&
            String(value)
              .toLowerCase()
              .includes(String(filterValue).toLowerCase())
        );
      },
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="px-6 flex flex-col">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-end items-center my-10 container mx-auto px-0 max-w-6xl pb-10 pt-2">
        {children && <div className="flex items-center gap-2">{children}</div>}
      </div>
    </div>
  );
};

export default AnalyticsDataTable;
